import type { StatusCategory } from "@beacon/core/projects"
import { useLayoutEffect, useRef, type KeyboardEvent, type ReactNode } from "react"
import { cn } from "@/primitives/utils"
import { edgeFadeOf } from "./edge-fade.ts"
import type { StatusStep } from "./ticket-glyphs.ts"
import { StatusIcon } from "./ticket-glyphs.tsx"

/*
 * Which Status a phone's Board shows, and the way to another: a row of every Status, each its
 * circle, its name and how many cards it holds, the one shown on the selected fill. It stands
 * above the pages it switches between, scrolls sideways when the Statuses run past the screen,
 * fading each edge with more past it, only as wide as what is past it, and keeps the one shown in
 * view. A screen reader hears a tab list over the pages, each a tab panel; the arrow keys, Home
 * and End move between the Statuses as tabs do.
 */

/** A Status as the switcher shows it: its circle (`statusGlyphOf`), its name, and its count. */
export type SwitcherStatus = {
  id: string
  name: string
  category: StatusCategory
  step?: StatusStep
  count: number
}

/**
 * `shown` is the Status on screen, by its id, and `onShow` asks for another. `panelOf` names the
 * page each Status's tab shows, by its element id, and the tab is that id with `-tab` after it, so
 * the page can say which tab labels it. `end` stands at the row's end, as the Board's ⋯ does.
 */
export function StatusSwitcher({
  statuses,
  shown,
  onShow,
  panelOf,
  end,
}: {
  statuses: readonly SwitcherStatus[]
  shown: string
  onShow: (statusId: string) => void
  panelOf: (statusId: string) => string
  end?: ReactNode
}) {
  const strip = useRef<HTMLDivElement>(null)
  const settled = useRef(false)

  // The tab shown stays in view, centred where the row can scroll that far: at once as the
  // switcher arrives, and smoothly as the Member moves to another.
  useLayoutEffect(() => {
    const row = strip.current
    const tab = row?.querySelector<HTMLElement>(`[data-status="${CSS.escape(shown)}"]`)
    if (!row || !tab) return
    const smooth =
      settled.current && !matchMedia("(prefers-reduced-motion: reduce)").matches ? "smooth" : "auto"
    settled.current = true
    row.scrollTo({
      left: tab.offsetLeft - (row.clientWidth - tab.offsetWidth) / 2,
      behavior: smooth,
    })
  }, [shown])

  // An edge with more Statuses past it fades, so a tab it cuts reads as more to scroll to rather
  // than as clipped, and only as wide as what is past it: centring can stop a few pixels along,
  // and a full fade there would wash out a tab that is wholly on screen. `data-fade` says which
  // edges, and `--fade-start-width` / `--fade-end-width` how wide, as the row scrolls, resizes or
  // its tabs change.
  const tabs = statuses.map(({ id }) => id).join("\n")
  useLayoutEffect(() => {
    const row = strip.current
    if (!row) return
    const measure = () => {
      const { fade, start, end } = edgeFadeOf(row)
      row.dataset.fade = fade
      row.style.setProperty("--fade-start-width", `${start}px`)
      row.style.setProperty("--fade-end-width", `${end}px`)
    }
    measure()
    const resized = new ResizeObserver(measure)
    resized.observe(row)
    for (const tab of row.children) resized.observe(tab)
    row.addEventListener("scroll", measure, { passive: true })
    return () => {
      resized.disconnect()
      row.removeEventListener("scroll", measure)
    }
  }, [tabs])

  const step = (event: KeyboardEvent) => {
    const at = statuses.findIndex(({ id }) => id === shown)
    const to =
      event.key === "ArrowRight"
        ? at + 1
        : event.key === "ArrowLeft"
          ? at - 1
          : event.key === "Home"
            ? 0
            : event.key === "End"
              ? statuses.length - 1
              : undefined
    const next = to === undefined ? undefined : statuses[to]
    if (!next) return
    event.preventDefault()
    onShow(next.id)
    strip.current?.querySelector<HTMLElement>(`[data-status="${CSS.escape(next.id)}"]`)?.focus()
  }

  return (
    <div className="flex h-12 shrink-0 items-center gap-1 border-b border-border-subtle bg-background pr-2 pl-4">
      <div
        ref={strip}
        role="tablist"
        aria-label="Statuses"
        onKeyDown={step}
        className="no-scrollbar relative -my-1 flex min-w-0 flex-1 gap-1 overflow-x-auto py-1 data-[fade=both]:fade-x-both data-[fade=end]:fade-x-end data-[fade=start]:fade-x-start"
      >
        {statuses.map((status) => {
          const current = status.id === shown
          return (
            <button
              key={status.id}
              type="button"
              role="tab"
              id={`${panelOf(status.id)}-tab`}
              data-status={status.id}
              aria-selected={current}
              aria-controls={panelOf(status.id)}
              tabIndex={current ? 0 : -1}
              onClick={() => onShow(status.id)}
              className="touch-target flex h-9 shrink-0 items-center gap-1.5 rounded-md px-2.5 text-ui font-medium whitespace-nowrap text-muted-foreground outline-none hover:bg-accent hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring aria-selected:bg-selected aria-selected:text-foreground"
            >
              <StatusIcon category={status.category} {...(status.step && { step: status.step })} />
              {status.name}
              <span
                className={cn(
                  "text-xs font-normal tabular-nums",
                  current ? "text-muted-foreground" : "text-subtle-foreground",
                )}
              >
                {status.count}
              </span>
            </button>
          )
        })}
      </div>
      {end}
    </div>
  )
}
